import { useEffect, useRef, useState } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { Leaf, Phone, X, Lock } from "lucide-react";
import { useMobileNavOpen } from "@/hooks/useMobileNavOpen";
import { useMembership } from "@/hooks/useMembership";
import { EXERCISES as GROUNDING_EXERCISES } from "@/data/grounding-exercises";
import { CreateAccountCTA } from "@/components/CreateAccountCTA";


// Routes where the floating button would overlap large mobile CTAs
// (Save Reflection, Save entry, Send, etc.) and intercept taps.
const HIDE_ON_ROUTES = [
  "/journal",
  "/dreams",
  "/guided-journal",
  "/sleep-dreams",
  "/check-in",
  "/mood",
  "/community",
  "/sanctuary", // Has its own Ground me link.
  "/grief-loss", // This page has its own visible Ground me link.
  "/support",
  "/shadow-work",
  "/therapist-application",
  "/therapist-availability",
  // The button's whole purpose is grounding — don't float it on pages that
  // already make grounding the primary action.
  "/grounding",
  "/breathing",
  "/somatic",
  "/meditations",
  "/sounds",
  "/tapping",
  "/affirmations",
  "/bilateral",
  "/crisis",
];

// How long the floating button stays hidden after the user taps the ×.
const HIDE_KEY = "rt:hide-ground-me";
const HIDE_MS = 2 * 60 * 60 * 1000; // a couple of hours

export function GroundMeNow() {
  const { isMember, loading } = useMembership();
  // Keep the server and first browser render identical while membership is
  // resolving. Switching button → link during hydration can discard a tap on
  // another control elsewhere on the page, including guest audio controls.
  const locked = loading || !isMember;
  const [open, setOpen] = useState(false);
  const [obstructed, setObstructed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const until = Number(window.localStorage.getItem(HIDE_KEY) ?? 0);
      setDismissed(Number.isFinite(until) && until > Date.now());
    } catch {
      /* storage unavailable — keep the button visible */
    }
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(HIDE_KEY, String(Date.now() + HIDE_MS));
    } catch {
      /* still hide for this page view */
    }
    setDismissed(true);
  };
  const router = useRouter();
  const pathname = router.state.location.pathname;
  const onHome = pathname === "/";
  const onSounds = pathname === "/sounds";
  const navOpen = useMobileNavOpen();
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const hidden =
    navOpen ||
    pathname === "/admin" ||
    pathname.startsWith("/admin/") ||
    pathname.startsWith("/admin-") ||
    HIDE_ON_ROUTES.some((r) => pathname === r || pathname.startsWith(r + "/"));
  const [lift, setLift] = useState(0);


  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  // Collision rule: hide the floating button whenever it would overlap any
  // other interactive element (button/link) at its target on-screen position.
  useEffect(() => {
    if (hidden) return;
    const check = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const isMobile = vw < 640;
      const safe = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue("--sai-b") || "0",
      ) || 0;
      // Mirror the button's CSS placement (right edge, near bottom).
      const width = wrapRef.current?.offsetWidth || (isMobile ? 104 : 156);
      const height = wrapRef.current?.offsetHeight || (isMobile ? 28 : 44);
      const right = isMobile ? 12 : 20;
      const bottomOffset = onSounds
        ? (isMobile ? 128 : 96)
        : isMobile
          ? safe + 132
          : 20;
      const collidesAt = (offset: number) => {
        const x1 = vw - right - width;
        const y1 = vh - bottomOffset - height - offset;
        const x2 = vw - right;
        const y2 = vh - bottomOffset - offset;
        if (y1 < 80) return true;
        const pts = [[x1 + 4, y1 + 4], [x2 - 4, y1 + 4], [x1 + 4, y2 - 4], [x2 - 4, y2 - 4], [(x1 + x2) / 2, (y1 + y2) / 2]] as const;
        return pts.some(([x, y]) => document.elementsFromPoint(x, y).some(el =>
          !el.closest('[data-floating-control="ground-me"]') &&
          !!el.closest("a, button, [role=button], input, textarea, select, [data-no-overlap]")));
      };
      // Move up to the nearest clear spot instead of vanishing; fade only if none is free.
      const free = [0, 56, 112, 168, 224, 280].find(o => !collidesAt(o));
      const collides = free === undefined;
      setLift(free ?? 0);
      // Only actionable controls and explicitly protected areas should make
      // this safety control move out of the way. Ordinary reading content must
      // never make it disappear.
      setObstructed(collides);
    };
    check();
    const onScroll = () => check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    const id = window.setInterval(check, 600);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.clearInterval(id);
    };
  }, [hidden, onHome, onSounds, pathname]);

  if (hidden || dismissed) return null;


  const wrapClass = `fixed right-3 sm:right-5 z-[45] transition-opacity duration-300 ${onSounds ? "bottom-32 sm:bottom-24" : "sm:!bottom-8"} ${obstructed ? "pointer-events-none opacity-0 translate-y-2" : "opacity-100"}`;
  const wrapStyle = { bottom: onSounds ? undefined : "calc(env(safe-area-inset-bottom, 0px) + 164px)", transform: lift ? `translateY(-${lift}px)` : undefined };
  const btnClass = `group relative inline-flex min-h-10 items-center gap-1 rounded-full border border-[var(--gold-deep)] bg-[var(--gradient-gold)] px-3 py-2 shadow-[0_6px_16px_-6px_rgba(184,134,11,0.55),inset_0_1px_1px_rgba(255,255,255,0.55)] ring-1 ring-background/70 transition-transform duration-300 hover:scale-105 active:scale-95 sm:gap-1.5 sm:border-2 sm:px-4 sm:py-2.5 sm:ring-2`;
  const btnInner = (
    <>
      <span aria-hidden className="pointer-events-none absolute -inset-1 -z-10 rounded-full bg-gold/30 blur-xl transition-colors duration-500 group-hover:bg-gold/50" />
      <span className="relative flex h-4 w-4 items-center justify-center rounded-full bg-background/85 shadow-sm sm:h-7 sm:w-7">
        <Leaf className="h-2.5 w-2.5 text-[var(--gold-deep)] sm:h-4 sm:w-4" strokeWidth={2.25} />
        {locked && (
          <span className="absolute -bottom-1 -end-1 flex h-2.5 w-2.5 items-center justify-center rounded-full border border-background bg-foreground sm:h-3.5 sm:w-3.5">
            <Lock className="h-1.5 w-1.5 text-background sm:h-2.5 sm:w-2.5" strokeWidth={3} />
          </span>
        )}
      </span>
      <span className="relative font-display text-[9px] font-semibold uppercase tracking-[0.08em] text-foreground sm:text-xs sm:tracking-[0.12em]">
        {locked ? "Grounding" : "Ground Me"}
      </span>
    </>
  );

  return (
    <>
      <div ref={wrapRef} data-ground-me="" data-floating-control="ground-me" className={wrapClass} style={wrapStyle}>
        {locked ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Ground Me"
            aria-hidden={obstructed || undefined}
            tabIndex={obstructed ? -1 : 0}
            className={btnClass}
          >
            {btnInner}
          </button>
        ) : (
          <Link
            to="/grounding"
            aria-label="Ground Me"
            aria-hidden={obstructed || undefined}
            tabIndex={obstructed ? -1 : 0}
            className={btnClass}
          >
            {btnInner}
          </Link>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hide the grounding button for 2 hours"
          title="Hide for 2 hours"
          className="absolute -top-2 -end-2 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition hover:bg-accent hover:text-foreground"
        >
          <X className="h-3 w-3" strokeWidth={2.5} />
        </button>
      </div>


      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-gold/30 bg-card p-6 shadow-2xl sm:rounded-2xl"
          >
            {/* Header */}
            <div className="mb-5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/30 bg-[var(--sage,#E6F0E8)] px-2.5 py-0.5 text-xs font-medium text-foreground/80">
                  <Leaf className="h-3 w-3" /> You are safe right now
                </div>
                <h2 className="mt-3 font-serif text-2xl leading-snug text-foreground">
                  💛 You don't have to carry this alone.
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Take a moment. Choose one gentle exercise below and give
                  yourself permission to slow down.
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-accent"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Members never see this sheet — the button links straight to
                /grounding#grounding-exercises. This is the guest/locked view:
                the real exercise names are shown, but each one stays locked. */}
            <ul className="space-y-2">
              {GROUNDING_EXERCISES.map((ex) => (
                <li key={ex.id}>
                  <Link
                    to="/membership"
                    aria-label={`${ex.title} — members only`}
                    onClick={() => setOpen(false)}
                    className="flex min-h-11 items-center gap-3 rounded-xl border border-gold/25 bg-card/60 px-3 py-2.5 text-start transition-colors hover:bg-accent/60"
                  >
                    <span aria-hidden className="shrink-0 text-lg">{ex.emoji}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">
                        {ex.title}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {ex.duration} · {ex.blurb}
                      </span>
                    </span>
                    <Lock aria-hidden className="h-4 w-4 shrink-0 text-[var(--gold-deep)]" strokeWidth={2} />
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-3 rounded-2xl border-2 border-gold/40 bg-gradient-to-br from-gold-light/40 to-card p-5 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
                <Lock className="h-5 w-5 text-foreground" strokeWidth={2.25} />
              </div>
              <h3 className="mt-3 font-display text-base text-foreground">
                These grounding exercises are members-only 💛
              </h3>
              <p className="mx-auto mt-2 max-w-sm text-xs leading-relaxed text-muted-foreground">
                Unlock all {GROUNDING_EXERCISES.length} guided exercises with voice, video and
                step-by-step support. Create your account to begin.
              </p>
              <div className="mt-4 flex justify-center">
                <CreateAccountCTA />
              </div>
            </div>



            {/* Encouragement footer */}
            <div className="mt-5 rounded-2xl border border-gold/30 bg-cream/50 p-4 text-center">
              <p className="font-serif text-base text-foreground">
                🌱 Healing happens one moment at a time.
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                You do not need to complete everything. Even one exercise is
                a meaningful step.
              </p>
            </div>

            {/* Crisis section */}
            <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/5 p-3.5">
              <div className="flex items-start gap-2.5">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div className="text-xs leading-relaxed text-foreground">
                  <p className="font-semibold">Recovery Trauma™ is not a crisis service.</p>
                  <p className="mt-0.5 text-muted-foreground">
                    If you are in immediate danger, experiencing thoughts of
                    self-harm, or require urgent support, contact local
                    emergency services or an appropriate crisis service.
                  </p>
                  <Link
                    to="/crisis"
                    onClick={() => setOpen(false)}
                    className="mt-1.5 inline-block font-semibold text-destructive underline"
                  >
                    View emergency resources →
                  </Link>
                </div>
              </div>
            </div>

            <button
              onClick={() => setOpen(false)}
              className="mt-4 w-full rounded-full border border-border bg-background py-2.5 text-sm font-medium text-foreground hover:bg-accent"
            >
              I feel a little steadier
            </button>
          </div>
        </div>
      )}
    </>
  );
}
